// src/components/CotationWizard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CONFIG } from '../config/config';

import StepConsultation from './CotationSteps/StepConsultation';
import { mklog, dbgFetch, debugEnabled } from '../utils/debug';

import { domainOf, normalizeCodes as normalizeCodesApi, sheetSyncAndPoll } from '../api/cotations';
import { getDisplayMusiqId } from '../utils/musiqId';

export default function CotationWizard({
  embedded = false,
  passageId,
  token,
  patients = [],
  setPatients,
  onDone,
  currentPage,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const log = mklog('WIZ');

  const id = String(passageId ?? params.id ?? '');
  const authToken = token ?? sessionStorage.getItem('token') ?? localStorage.getItem('token');

  const [patient, setPatient] = useState(null);

  const enCoursRef = useRef(false);

  const [resumeSauvegarde, setResumeSauvegarde] = useState([]);
  const [pret, setPret] = useState(false);

  /* ---------- Load patient ---------- */
  useEffect(() => {
    if (!id) return;
    log('mount/load patient', { id, fromParams: params?.id, token: Boolean(authToken) });

    const inStore = Array.isArray(patients)
      ? patients.find((p) => String(p.id) === String(id))
      : null;
    if (inStore) {
      setPatient(inStore);
      log('patient <- store', {
        id: inStore.id,
        step: inStore.currentStep ?? inStore.current_step,
        salle: inStore.salle,
      });
      return;
    }
    dbgFetch('WIZ', `${CONFIG.API_URL}/passages/${id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => setPatient(json || null))
      .catch(() => {});
  }, [id, authToken, patients]);

  useEffect(() => {
    if (!id || !authToken) return;
    let annule = false;
    setPret(false);

    const charger = async () => {
      let resume = null;

      try {
        const res = await dbgFetch('WIZ', `${CONFIG.API_URL}/passages/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const j = await res.json();
          resume = j?.cotation_json?.result?.resume ?? null;
        }
      } catch {}

      // repli : le snapshot gardé dans le navigateur par finalize
      if (!resume) {
        try {
          resume = JSON.parse(localStorage.getItem(`musiq:cotation:${id}`))?.resume ?? null;
        } catch {}
      }

      // on ne garde que les nouveaux parcours, pas les anciennes lignes { code, libelle }
      const valide = Array.isArray(resume) && resume.every((e) => e && 'next' in e);

      if (!annule) {
        setResumeSauvegarde(valide ? resume : []);
        setPret(true);
      }
    };

    charger();
    return () => {
      annule = true;
    };
  }, [id, authToken]);

  const finalize = useCallback(
    async ({ parcours, codes, redirectSortie }) => {
      if (!id || !authToken || enCoursRef.current) return;
      enCoursRef.current = true;

      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` };
      const codesString = codes.join('+');

      try {
        // pas de tarifs pour l'instant : totaux à 0, le parcours sert de résumé
        const totals = { ccam: 0, dep: 0, total: 0 };
        const snap = {
          version: 2,
          savedAt: new Date().toISOString(),
          passageId: id,
          domaine: null,
          actes: codes,
          majorations: [],
          depassement: 0,
          resume: parcours,
          totals,
          codes: codesString,
        };
        log('finalize -> snapshot', snap);

        // on enregistre d'abord, pour ne jamais passer en "complete" sans cotation en base
        const resSave = await dbgFetch(
          'WIZ',
          `${CONFIG.API_URL}/cotation/passages/${id}/cotation/save`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              domaine: snap.domaine,
              actes: snap.actes,
              majorations: snap.majorations,
              codes: snap.codes,
              result: { resume: snap.resume, totals: snap.totals, total: 0 },
            }),
          },
        );
        if (!resSave.ok) throw new Error(`Save cotation failed: ${resSave.status}`);

        const resStatus = await dbgFetch('WIZ', `${CONFIG.API_URL}/passages/${id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            cotationStatus: 'complete',
            ...(redirectSortie && { currentStep: 7 }),
          }),
        });

        if (!resStatus.ok) {
          const txt = await resStatus.text().catch(() => '');
          throw new Error(`Status update failed: ${resStatus.status} ${txt}`);
        }

        try {
          localStorage.setItem(`musiq:cotation:${id}`, JSON.stringify(snap));
        } catch {}

        sheetSyncAndPoll({
          passageId: id,
          codes: normalizeCodesApi(codesString),
          token: authToken,
          domain: domainOf(patient),
          musiqId: getDisplayMusiqId(patient),
          isDemo: Boolean(patient?.is_demo),
        }).catch((e) => console.error(e));

        window.dispatchEvent(
          new CustomEvent('musiq:cotation:finalized', { detail: { id, snapshot: snap } }),
        );

        if (typeof setPatients === 'function') {
          setPatients((ps) =>
            ps.map((p) =>
              String(p.id) === String(id)
                ? {
                    ...p,
                    cotationStatus: 'complete',
                    cotation_status: 'complete',
                    codes: codesString,
                    ...(redirectSortie && { currentStep: 7 }),
                  }
                : p,
            ),
          );
        }

        if (onDone || embedded) onDone?.();
        else navigate('/sortie?cotation=' + id);
      } catch (err) {
        console.error('Erreur finalize:', err);
        log('finalize error', err?.message);
        alert('Impossible de valider la cotation : ' + err.message);
      } finally {
        enCoursRef.current = false;
      }
    },
    [id, authToken, patient, setPatients, onDone, embedded, navigate],
  );

  return (
    <div className="max-w-[980px] mx-auto">
      <div className="p-4">
        {pret && (
          <StepConsultation
            key={id}
            data={{ parcours: resumeSauvegarde }}
            finalize={finalize}
            currentPage={currentPage}
          />
        )}
      </div>
    </div>
  );
}
